import type { Module } from 'vuex'
import dayjs from 'dayjs'
import { customerActions } from './actions'
import type { PrunedActivity, CustomerState, PrunedCustomer, PrunedPurchase, LoyaltyProgramTiersMap, MessageListSubscription } from './types'
import { formatCustomerLocation } from './utils'
import type { RootState } from '@/store/modules/types'
import type { Campaign } from '@/types/resources/campaign'
import { clone } from '@/utils/helpers'
import type { Event } from '@/types/resources/event'
import type { CustomerCustomFields, FanLoyaltyMembership, LoyaltyProgram } from '@/api/customer-profile/types'
import { countryList } from '@/utils/countries'
import { formatFanName } from '@/store/modules/audience/utils'

export const initialCustomerState = (): CustomerState => ({
  customer: null,
  customerCustomFields: null,
  isFetchingCustomer: false,
  isFetchingCustomerCustomFields: false,
  hasFetchCustomerFailed: false,

  // Customer Tags
  fanTags: [],
  isFetchingFanTags: false,

  // Customer Message List Subscriptions
  fanMessageListSubscriptions: [],
  isFetchingFanMessageListSubscriptions: false,

  // Customer Activities
  fanActivity: [], // Formatted activity data for legacy components
  rawFanActivity: [], // Raw server activity data with complete additionalInfo
  noMoreActivity: false,
  isFetchingActivity: false,
  hasFetchCustomerActivityFailed: false,
  lastActivityType: 'important',
  pendingActivityCount: 0,
  pendingActivityBaselineOids: [],

  // Customer Purchase
  fanPurchases: [],
  totalPurchasesCount: 0,
  noMorePurchases: false,
  isFetchingPurchases: false,
  hasFetchCustomerPurchasesFailed: false,

  // Customer Events
  fanEvents: [],
  actionedEventOids: [],
  isFetchingEvents: false,
  hasFetchCustomerEventsFailed: false,

  // Customer Campaigns
  fanCampaigns: [],
  actionedCampaignOids: [],
  customerCampaignCount: null,
  isFetchingCampaigns: false,
  hasFetchCustomerCampaignsFailed: false,

  // Customer Loyalty Memberships
  fanLoyaltyMemberships: [],
  isFetchingLoyaltyMemberships: false,
  loyaltyProgramTiersMap: {},

  // Customer Loyalty Programs
  fanLoyaltyPrograms: [],
  isFetchingLoyaltyPrograms: false,

  // Customer Message Summary
  customerMessageSummary: null,
  isFetchingCustomerMessageSummary: false,

  // Customer Message Totals (all-time, channel-combined, sourced from MFPAM)
  customerMessageTotals: null,
  isFetchingCustomerMessageTotals: false,
})

const customerModule: Module<CustomerState, RootState> = {
  namespaced: true,
  state: initialCustomerState,
  actions: customerActions,
  mutations: {
    // Resets all customer data so that you don't need to call multiple reset methods
    RESET_CUSTOMER_DATA(state) {
      state.customer = null
      state.fanTags = []
      state.fanMessageListSubscriptions = []
      state.isFetchingFanMessageListSubscriptions = false
      state.noMoreActivity = false
      state.hasFetchCustomerFailed = false
      state.fanActivity = []
      state.pendingActivityCount = 0
      state.pendingActivityBaselineOids = []
      state.fanEvents = []
      state.actionedEventOids = []
      state.hasFetchCustomerEventsFailed = false
      state.fanCampaigns = []
      state.actionedCampaignOids = []
      state.customerCampaignCount = null
      state.hasFetchCustomerCampaignsFailed = false
      state.fanPurchases = []
      state.noMorePurchases = false
      state.hasFetchCustomerPurchasesFailed = false
      state.fanLoyaltyMemberships = []
      state.isFetchingLoyaltyMemberships = false
      state.loyaltyProgramTiersMap = {}
      state.fanLoyaltyPrograms = []
      state.isFetchingLoyaltyPrograms = false
      state.customerCustomFields = null
      state.customerMessageSummary = null
      state.isFetchingCustomerMessageSummary = false
      state.customerMessageTotals = null
      state.isFetchingCustomerMessageTotals = false
    },
    // For fetching fans
    RESET_CUSTOMERS(state) {
      state.customer = null
    },
    SET_IS_FETCHING_CUSTOMERS(state, isFetchingCustomer: boolean) {
      state.isFetchingCustomer = isFetchingCustomer
    },
    SET_IS_FETCHING_CUSTOMERS_CUSTOM_FIELDS(state, isFetchingCustomerCustomFields: boolean) {
      state.isFetchingCustomerCustomFields = isFetchingCustomerCustomFields
    },

    SET_CUSTOMER(state, customer: PrunedCustomer) {
      state.customer = customer
    },
    SET_CUSTOMER_CUSTOM_FIELDS(state, customerCustomFields: CustomerCustomFields) {
      state.customerCustomFields = customerCustomFields
    },
    UPDATE_CUSTOMER_CUSTOM_FIELDS(state, changes: { [key: string]: any }) {
      const existing = state.customerCustomFields
      state.customerCustomFields = {
        ...(existing ?? {}),
        customFieldString: { ...existing?.customFieldString, ...changes.customFieldString },
        customFieldInteger: { ...existing?.customFieldInteger, ...changes.customFieldInteger },
        customFieldDatetime: { ...existing?.customFieldDatetime, ...changes.customFieldDatetime },
        customFieldOptions: { ...existing?.customFieldOptions, ...changes.customFieldOptions },
      } as CustomerCustomFields
    },
    UPDATE_CUSTOMER(state, changes: { [key: string]: any }) {
      const combinedData = {
        ...state.customer,
        ...changes,
      }
      // @ts-ignore
      const name = formatFanName(combinedData)
      // @ts-ignore
      const formattedLocation = formatCustomerLocation(combinedData)
      // @ts-ignore
      state.customer = {
        ...state.customer,
        ...changes,
        name,
        formattedLocation,
      }
    },
    SET_HAS_FETCH_CUSTOMER_FAILED(state, hasFailed: boolean) {
      state.hasFetchCustomerFailed = hasFailed
    },

    // Customer Tags
    RESET_CUSTOMER_TAGS(state) {
      state.fanTags = []
    },
    SET_CUSTOMER_TAGS(state, tags: Tag[]) {
      // Note - sometimes fan_promoter_tags are not correctly removed, and this leads to blank tags appearing.
      // Until this issue is fully resolved, lets hide these tags from the UI.
      state.fanTags = clone(tags.filter(item => !!item.oid))
    },
    SET_IS_FETCHING_CUSTOMER_TAGS(state, isFetching: boolean) {
      state.isFetchingFanTags = isFetching
    },
    REMOVE_FROM_CUSTOMER_TAGS(state, tagOid: number) {
      state.fanTags = state.fanTags.filter((tag) => {
        return tag.oid !== tagOid
      })
    },

    // Customer Message List Subscriptions
    RESET_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS(state) {
      state.fanMessageListSubscriptions = []
    },
    SET_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS(state, subscriptions: MessageListSubscription[]) {
      state.fanMessageListSubscriptions = clone(subscriptions)
    },
    SET_IS_FETCHING_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS(state, isFetching: boolean) {
      state.isFetchingFanMessageListSubscriptions = isFetching
    },

    // Customer Activities
    // Note: deliberately does NOT touch pendingActivityCount/pendingActivityBaselineOids - this fires on
    // every in-place refresh (post-save, filter change, tab mount), and a pending wait must survive those.
    // Only MARK_PENDING_ACTIVITY/CLEAR_PENDING_ACTIVITY and RESET_CUSTOMER_DATA (customer switch/teardown)
    // should change them.
    RESET_CUSTOMER_ACTIVITY(state) {
      state.noMoreActivity = false
      state.hasFetchCustomerActivityFailed = false
      state.fanActivity = []
      state.rawFanActivity = []
    },
    // Marks that `count` activity rows are expected soon (e.g. a subscription edit was just saved) so the
    // activity list can show loading placeholders until the server-side processing catches up.
    MARK_PENDING_ACTIVITY(state, count: number) {
      state.pendingActivityCount = count
      state.pendingActivityBaselineOids = state.rawFanActivity.map((activity: any) => activity?.oid)
    },
    CLEAR_PENDING_ACTIVITY(state) {
      state.pendingActivityCount = 0
      state.pendingActivityBaselineOids = []
    },
    CONCAT_CUSTOMER_ACTIVITY(state, activityList: PrunedActivity[]) {
      state.fanActivity = clone(state.fanActivity.concat(activityList))
    },
    CONCAT_RAW_CUSTOMER_ACTIVITY(state, rawActivityList: unknown[]) {
      state.rawFanActivity = clone(state.rawFanActivity.concat(rawActivityList))
    },
    // Replace allows us to swap out activity lists in one operation, allowing a clean animated transition to occur
    REPLACE_CUSTOMER_ACTIVITY(state, activityList: PrunedActivity[]) {
      state.fanActivity = activityList
    },
    REPLACE_RAW_CUSTOMER_ACTIVITY(state, rawActivityList: unknown[]) {
      state.rawFanActivity = rawActivityList
    },
    SET_IS_NO_MORE_CUSTOMER_ACTIVITY(state, isNoMoreActivity: boolean) {
      state.noMoreActivity = isNoMoreActivity
    },
    SET_IS_FETCHING_CUSTOMER_ACTIVITY(state, isFetchingActivity: boolean) {
      state.isFetchingActivity = isFetchingActivity
    },
    SET_HAS_FETCH_CUSTOMER_ACTIVITY_FAILED(state, hasFailed: boolean) {
      state.hasFetchCustomerActivityFailed = hasFailed
    },
    SET_LAST_ACTIVITY_TYPE(state, activityType: string) {
      state.lastActivityType = activityType
    },

    // Customer Events
    RESET_CUSTOMER_EVENTS(state) {
      state.fanEvents = []
      state.actionedEventOids = []
      state.hasFetchCustomerEventsFailed = false
    },
    CONCAT_CUSTOMER_EVENTS(state, eventList: Event[]) {
      state.fanEvents = clone(state.fanEvents.concat(eventList))
    },
    CONCAT_CUSTOMER_ACTIONED_EVENT_OIDS(state, eventOids: number[]) {
      state.actionedEventOids = clone(state.actionedEventOids.concat(eventOids))
    },
    SET_IS_FETCHING_CUSTOMER_EVENTS(state, isFetchingEvents: boolean) {
      state.isFetchingEvents = isFetchingEvents
    },
    SET_HAS_FETCH_CUSTOMER_EVENTS_FAILED(state, hasFailed: boolean) {
      state.hasFetchCustomerEventsFailed = hasFailed
    },

    // Customer Campaigns
    RESET_CUSTOMER_CAMPAIGNS(state) {
      state.fanCampaigns = []
      state.actionedCampaignOids = []
      state.customerCampaignCount = null
      state.hasFetchCustomerCampaignsFailed = false
    },
    CONCAT_CUSTOMER_CAMPAIGNS(state, campaignList: Campaign[]) {
      state.fanCampaigns = clone(state.fanCampaigns.concat(campaignList))
    },
    CONCAT_CUSTOMER_ACTIONED_CAMPAIGN_OIDS(state, campaignOids: number[]) {
      state.actionedCampaignOids = clone(state.actionedCampaignOids.concat(campaignOids))
    },
    SET_CUSTOMER_CAMPAIGN_COUNT(state, count: number) {
      state.customerCampaignCount = count
    },
    SET_IS_FETCHING_CUSTOMER_CAMPAIGNS(state, isFetchingCampaigns: boolean) {
      state.isFetchingCampaigns = isFetchingCampaigns
    },
    SET_HAS_FETCH_CUSTOMER_CAMPAIGNS_FAILED(state, hasFailed: boolean) {
      state.hasFetchCustomerCampaignsFailed = hasFailed
    },

    // Customer Purchase
    RESET_CUSTOMER_PURCHASES(state) {
      state.fanPurchases = []
      state.totalPurchasesCount = 0
      state.noMorePurchases = false
      state.hasFetchCustomerPurchasesFailed = false
    },
    CONCAT_CUSTOMER_PURCHASES(state, purchasesList: PrunedPurchase[]) {
      state.fanPurchases = clone(state.fanPurchases.concat(purchasesList))
    },
    SET_TOTAL_PURCHASES_COUNT(state, count: number) {
      state.totalPurchasesCount = count
    },
    SET_IS_NO_MORE_CUSTOMER_PURCHASES(state, isNoMorePurchases: boolean) {
      state.noMorePurchases = isNoMorePurchases
    },
    SET_IS_FETCHING_CUSTOMER_PURCHASES(state, isFetchingPurchases: boolean) {
      state.isFetchingPurchases = isFetchingPurchases
    },
    SET_HAS_FETCH_CUSTOMER_PURCHASES_FAILED(state, hasFailed: boolean) {
      state.hasFetchCustomerPurchasesFailed = hasFailed
    },

    // Customer Loyalty Memberships
    RESET_CUSTOMER_LOYALTY_MEMBERSHIPS(state) {
      state.fanLoyaltyMemberships = initialCustomerState().fanLoyaltyMemberships
    },
    SET_CUSTOMER_LOYALTY_MEMBERSHIPS(state, fanLoyaltyMemberships: FanLoyaltyMembership[]) {
      state.fanLoyaltyMemberships = clone(fanLoyaltyMemberships)
    },
    SET_IS_FETCHING_CUSTOMER_LOYALTY_MEMBERSHIPS(state, isFetching: boolean) {
      state.isFetchingLoyaltyMemberships = isFetching
    },
    RESET_CUSTOMER_LOYALTY_PROGRAM_TIERS_MAP(state) {
      state.loyaltyProgramTiersMap = initialCustomerState().loyaltyProgramTiersMap
    },
    SET_CUSTOMER_LOYALTY_PROGRAM_TIERS_MAP(state, loyaltyProgramTiersMap: LoyaltyProgramTiersMap) {
      state.loyaltyProgramTiersMap = clone(loyaltyProgramTiersMap)
    },

    // Customer Loyalty Programs
    RESET_CUSTOMER_LOYALTY_PROGRAMS(state) {
      state.fanLoyaltyPrograms = initialCustomerState().fanLoyaltyPrograms
    },
    SET_CUSTOMER_LOYALTY_PROGRAMS(state, fanLoyaltyPrograms: LoyaltyProgram[]) {
      state.fanLoyaltyPrograms = clone(fanLoyaltyPrograms)
    },
    SET_IS_FETCHING_CUSTOMER_LOYALTY_PROGRAMS(state, isFetching: boolean) {
      state.isFetchingLoyaltyPrograms = isFetching
    },

    // Customer Message Summary
    SET_CUSTOMER_MESSAGE_SUMMARY(state, messageSummary: any) {
      state.customerMessageSummary = clone(messageSummary)
    },
    SET_IS_FETCHING_CUSTOMER_MESSAGE_SUMMARY(state, isFetching: boolean) {
      state.isFetchingCustomerMessageSummary = isFetching
    },
    RESET_CUSTOMER_MESSAGE_SUMMARY(state) {
      state.customerMessageSummary = null
      state.isFetchingCustomerMessageSummary = false
    },

    // Customer Message Totals (all-time, channel-combined, sourced from MFPAM)
    SET_CUSTOMER_MESSAGE_TOTALS(state, messageTotals: any) {
      state.customerMessageTotals = clone(messageTotals)
    },
    SET_IS_FETCHING_CUSTOMER_MESSAGE_TOTALS(state, isFetching: boolean) {
      state.isFetchingCustomerMessageTotals = isFetching
    },
    RESET_CUSTOMER_MESSAGE_TOTALS(state) {
      state.customerMessageTotals = null
      state.isFetchingCustomerMessageTotals = false
    },
  },
  getters: {
  },
}

export default customerModule
